import {injectable} from 'inversify'
import {action, makeObservable, observable} from 'mobx'
import {debounce} from 'lodash'
import dayjs from 'dayjs'

import {ICompanyDetailsStore} from './ICompanyDetailsStore'
import {fetchCompanyByName, fetchCompanyInfo} from './services'
import {
    CompanyInfoResponse,
    CompanyInfoProfile,
    CompanySearchResponse,
    getOnboardingStore
} from '@/stores/onboarding'

export interface CompanyOption {
    value: string
    label: string
}

@injectable()
export class CompanyDetailsStore implements ICompanyDetailsStore {
    isLoading = false
    searchText = ''
    companyOptions: CompanyOption[] = []
    isSearching = false
    isDissolvedCompany = false
    selectedCompany: CompanyInfoProfile | null = null
    forceUpdate = 0
    hasSearched = false
    isCompanyNameFocused = false
    isTradeNameFocused = false
    originalCompanyDetails: {
        companyName?: string
        entityType?: string
        companyTradeName?: string
        companyNumber?: string
        dateOfCreation?: string
        numberOfEmployees?: string
    } | null = null
    showResetConfirmation = false
    pendingFormData: any = null
    isAfterReset = false

    private debouncedSearch: (searchValue: string, entityType: string) => void

    constructor() {
        makeObservable(this, {
            isLoading: observable,
            searchText: observable,
            companyOptions: observable,
            isSearching: observable,
            isDissolvedCompany: observable,
            selectedCompany: observable,
            forceUpdate: observable,
            hasSearched: observable,
            isCompanyNameFocused: observable,
            isTradeNameFocused: observable,
            originalCompanyDetails: observable,
            showResetConfirmation: observable,
            pendingFormData: observable,
            isAfterReset: observable,

            searchCompaniesByName: action.bound,
            getCompanyInfo: action.bound,
            setSearchText: action.bound,
            setCompanyOptions: action.bound,
            setIsSearching: action.bound,
            setIsLoading: action.bound,
            setIsDissolvedCompany: action.bound,
            setSelectedCompany: action.bound,
            incrementForceUpdate: action.bound,
            setHasSearched: action.bound,
            setIsCompanyNameFocused: action.bound,
            setIsTradeNameFocused: action.bound,
            setOriginalCompanyDetails: action.bound,
            setShowResetConfirmation: action.bound,
            setPendingFormData: action.bound,
            setIsAfterReset: action.bound,
            searchCompanies: action.bound,
            handleCompanySelect: action.bound,
            resetCompanySearch: action.bound,
            clearCompanyOptions: action.bound,
            handleEntityTypeChange: action.bound,
            handleCompanyNameChange: action.bound,
            triggerDebouncedSearch: action.bound
        })

        this.debouncedSearch = debounce(this.searchCompanies.bind(this), 500)
    }

    /**
     * Search for companies by name
     * @param name Company name to search for
     * @returns Search results with matching companies
     */
    async searchCompaniesByName(name: string): Promise<CompanySearchResponse> {
        try {
            this.isLoading = true
            return await fetchCompanyByName(name)
        } catch (error: any) {
            console.error('Failed to search companies:', error)
            return {items: []}
        } finally {
            this.isLoading = false
        }
    }

    /**
     * Get detailed information for a specific company
     * @param companyId Company ID/Number to look up
     * @returns Company information details
     */
    async getCompanyInfo(companyId: string): Promise<CompanyInfoResponse> {
        try {
            this.isLoading = true

            return await fetchCompanyInfo(companyId)
        } catch (error: any) {
            console.error('Failed to get company info:', error)
            throw error
        } finally {
            this.isLoading = false
        }
    }

    setSearchText(text: string): void {
        this.searchText = text
    }

    setCompanyOptions(options: CompanyOption[]): void {
        this.companyOptions = options
    }

    setIsSearching(searching: boolean): void {
        this.isSearching = searching
    }

    setIsLoading(loading: boolean): void {
        this.isLoading = loading
    }

    setIsDissolvedCompany(dissolved: boolean): void {
        this.isDissolvedCompany = dissolved
    }

    setSelectedCompany(company: CompanyInfoProfile | null): void {
        this.selectedCompany = company
    }

    incrementForceUpdate(): void {
        this.forceUpdate += 1
    }

    setHasSearched(searched: boolean): void {
        this.hasSearched = searched
    }

    setIsCompanyNameFocused(focused: boolean): void {
        this.isCompanyNameFocused = focused
    }

    setIsTradeNameFocused(focused: boolean): void {
        this.isTradeNameFocused = focused
    }

    setOriginalCompanyDetails(
        details: {
            companyName?: string
            entityType?: string
            companyTradeName?: string
            companyNumber?: string
            dateOfCreation?: string
            numberOfEmployees?: string
        } | null
    ): void {
        this.originalCompanyDetails = details
    }

    setShowResetConfirmation(show: boolean): void {
        this.showResetConfirmation = show
    }

    setPendingFormData(data: any): void {
        this.pendingFormData = data
    }

    setIsAfterReset(afterReset: boolean): void {
        this.isAfterReset = afterReset
    }

    async searchCompanies(searchValue: string, entityType: string): Promise<void> {
        if (!searchValue || searchValue.length < 3) {
            this.setCompanyOptions([])
            this.setHasSearched(false)
            return
        }

        if (entityType !== 'company') {
            this.setCompanyOptions([])
            this.setHasSearched(false)
            return
        }

        this.setIsSearching(true)
        try {
            const response = await this.searchCompaniesByName(searchValue)

            if (response?.items?.length > 0) {
                const options = response.items.map((company: any) => ({
                    value: company.companyNumber,
                    label: `${company.title} (${company.companyNumber})`
                }))
                this.setCompanyOptions(options)
            } else {
                this.setCompanyOptions([])
            }
            this.setHasSearched(true)
        } catch (error) {
            this.setCompanyOptions([])
            this.setHasSearched(true)
            throw error
        } finally {
            this.setIsSearching(false)
        }
    }

    async handleCompanySelect(companyNumber: string): Promise<any> {
        const onboardingStore = getOnboardingStore()

        this.isLoading = true
        this.setIsDissolvedCompany(false)

        try {
            const companyInfo = await this.getCompanyInfo(companyNumber)

            if (companyInfo.companyProfile?.companyStatus === 'dissolved') {
                this.setIsDissolvedCompany(true)
                const companyName = companyInfo.companyProfile?.companyName || companyNumber
                this.setSearchText(companyName)
                this.setSelectedCompany(null)
                return {
                    companyName,
                    formValues: null,
                    isDissolvedCompany: true
                }
            }

            const companyName = companyInfo.companyProfile?.companyName || ''
            const companyTradeName = companyInfo.companyProfile?.companyTradeName || companyName
            const registrationNumber = companyInfo.companyProfile?.companyNumber || ''
            const rawDateOfCreation = companyInfo.companyProfile?.dateOfCreation || ''

            const dateOfCreation = onboardingStore.formatDateOfCreation(rawDateOfCreation)
            const entityType = onboardingStore.mapCompanyType('ltd')

            const selectedCompanyObj: CompanyInfoProfile = {
                companyNumber: registrationNumber,
                title: companyName,
                dateOfCreation: dateOfCreation,
                companyName: companyName,
                registeredOfficeAddress: companyInfo.companyProfile?.registeredOfficeAddress
            }

            const formValues = {
                companyName,
                companyTradeName,
                registrationNumber,
                dateOfCreation: onboardingStore.isValidDateForForm(dateOfCreation)
                    ? dayjs(dateOfCreation)
                    : null,
                entityType
            }

            this.setSearchText(companyName)
            this.setSelectedCompany(selectedCompanyObj)

            onboardingStore._originalCompanyInfo = {
                uberId: companyInfo.uberId,
                existingClient: companyInfo.existingClient,
                companyProfile: companyInfo.companyProfile,
                companyDirectors: companyInfo.companyDirectors,
                individualShareholders: companyInfo.individualShareholders,
                corporateEntityShareholders: companyInfo.corporateEntityShareholders
            }

            return {
                companyName,
                formValues,
                selectedCompany: selectedCompanyObj,
                isDissolvedCompany: false
            }
        } catch (error) {
            throw error
        } finally {
            this.isLoading = false
        }
    }

    resetCompanySearch(): void {
        this.setSelectedCompany(null)
        this.setHasSearched(false)
        this.setCompanyOptions([])
        this.setSearchText('')
    }

    clearCompanyOptions(): void {
        this.setCompanyOptions([])
        this.setHasSearched(false)
    }

    handleEntityTypeChange(): void {
        this.resetCompanySearch()
    }

    handleCompanyNameChange(value: string): void {
        this.setSearchText(value)

        if (this.selectedCompany) {
            this.setSelectedCompany(null)
        }

        if (this.isDissolvedCompany) {
            this.setIsDissolvedCompany(false)
        }
    }

    triggerDebouncedSearch(searchValue: string, entityType: string): void {
        this.debouncedSearch(searchValue, entityType)
    }
}
